// Step 22 — AI Buyer Intelligence Workspace.
//
// Two exports:
//   AIBuyers        — /deals/:workspaceId/buyers — comparison workspace
//   AIBuyerDetail   — /deals/:workspaceId/buyers/:acquirerId — full
//                     per-buyer intelligence workspace
//
// Both pages consume ONLY existing endpoints:
//   getDealSituationLive(workspaceId)                    (Step 12)
//   getBuyerIntelligence(acquirerId, {workspaceId})       (Step 11)
//   listDealActions(workspaceId, {acquirer_id})           (Step 13)
//
// Zero new backend endpoints, zero new domain models. Every value
// rendered is authoritative — no invented buyer scores, probabilities,
// or engagement rates.
//
// Workspace isolation stays server-side (endpoints re-validate via
// `for_user()`). The frontend never trusts a buyer/workspace id
// merely because it came from the URL — the API returns 404 for
// buyers outside the caller's workspaces.

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  getBuyerIntelligence,
  getDealSituationLive,
} from "../api";
import {
  AIEmpty,
  AIError,
  AILoading,
  AIPageHeader,
  AISection,
} from "../shell/AIPrimitives";
import { useAIWorkspace } from "../shell/AIWorkspaceContext";
import AIAskQadaButton from "../shell/intelligence/AIAskQadaButton";
import AIBuyerActions from "../shell/buyers/AIBuyerActions";
import AIBuyerActivityCard from "../shell/buyers/AIBuyerActivityCard";
import AIBuyerAttentionChips from "../shell/buyers/AIBuyerAttentionChips";
import AIBuyerComparisonTable from "../shell/buyers/AIBuyerComparisonTable";
import AIBuyerFindings from "../shell/buyers/AIBuyerFindings";
import AIBuyerIdentityCard from "../shell/buyers/AIBuyerIdentityCard";
import AIBuyerInterestCard from "../shell/buyers/AIBuyerInterestCard";
import AIBuyerPatternsCard from "../shell/buyers/AIBuyerPatternsCard";
import {
  buildBuyerAskPrompt,
  buildBuyerComparisonAskPrompt,
  crossPatternsForBuyer,
} from "../shell/buyers/helpers";


// ── /deals/:workspaceId/buyers ─────────────────────────────────────

export function AIBuyers() {
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ signal } = {}) => {
      if (!activeWorkspaceId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getDealSituationLive(activeWorkspaceId, { signal });
        setData(res);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda köpare.");
      } finally {
        setLoading(false);
      }
    },
    [activeWorkspaceId],
  );

  useEffect(() => {
    const c = new AbortController();
    load({ signal: c.signal });
    return () => c.abort();
  }, [load]);

  if (!activeWorkspaceId) {
    return <AIEmpty title="Ingen affär vald" />;
  }
  if (loading && !data) return <AILoading label="Laddar köparjämförelse…" />;

  const portfolio = data?.buyer_portfolio || [];
  const patterns = data?.cross_buyer_patterns || {};

  return (
    <>
      <AIPageHeader
        title="Köpare"
        subtitle={activeWorkspace?.organisation_name}
        actions={
          portfolio.length > 0 ? (
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={buildBuyerComparisonAskPrompt({
                workspaceName: activeWorkspace?.organisation_name,
              })}
              label="Fråga Qada om köparna"
            />
          ) : null
        }
      />
      <AIError message={error} />

      {portfolio.length === 0 ? (
        <AIEmpty
          title="Inga köpare registrerade ännu"
          hint="Köpare läggs till via broker-portalen; AI läser samma lista."
        />
      ) : (
        <AISection
          title="Jämförelse"
          right={
            <span className="qai-page-section-hint">
              {portfolio.length} köpar
              {portfolio.length === 1 ? "e" : "e"} · deterministisk data
            </span>
          }
        >
          <AIBuyerComparisonTable
            portfolio={portfolio}
            patterns={patterns}
            workspaceId={activeWorkspaceId}
          />
        </AISection>
      )}
    </>
  );
}


// ── /deals/:workspaceId/buyers/:acquirerId ─────────────────────────

export function AIBuyerDetail() {
  const { acquirerId } = useParams();
  const { activeWorkspaceId, activeWorkspace } = useAIWorkspace();
  const navigate = useNavigate();

  const [intel, setIntel] = useState(null);
  const [situation, setSituation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeWorkspaceId || !acquirerId) return;
    const c = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Two parallel reads:
        //   - full buyer intel (backend enforces workspace via ?workspace=)
        //   - workspace deal situation (for cross-buyer patterns + the
        //     buyer's row in the portfolio, which carries the silence
        //     flag that isn't in the per-buyer aggregate).
        const [i, s] = await Promise.all([
          getBuyerIntelligence(acquirerId, {
            workspaceId: activeWorkspaceId,
            signal: c.signal,
          }),
          getDealSituationLive(activeWorkspaceId, { signal: c.signal }),
        ]);
        setIntel(i);
        setSituation(s);
      } catch (e) {
        if (e?.name === "AbortError") return;
        setError(
          e?.body?.detail || e.message || "Kunde inte ladda köpar-intelligence.",
        );
      } finally {
        setLoading(false);
      }
    })();
    return () => c.abort();
  }, [activeWorkspaceId, acquirerId]);

  if (loading && !intel) return <AILoading label="Laddar köpare…" />;

  // Backend returns 404 for buyers outside the caller's workspaces —
  // in that case `intel` stays null and `error` is populated.
  if (!intel) {
    return (
      <>
        <AIPageHeader
          title={`Köpare #${acquirerId}`}
          actions={
            <button
              type="button"
              className="qai-da-btn"
              onClick={() => navigate(`/deals/${activeWorkspaceId}/buyers`)}
            >
              ← Alla köpare
            </button>
          }
        />
        <AIError message={error} />
        <AIEmpty title="Ingen data tillgänglig för den här köparen." />
      </>
    );
  }

  const acquirerIdNum = Number(acquirerId);
  const portfolioRow =
    (situation?.buyer_portfolio || []).find(
      (r) => Number(r.acquirer?.id) === acquirerIdNum,
    ) || null;
  const patterns = situation?.cross_buyer_patterns || {};
  const acquirerPatterns = crossPatternsForBuyer(patterns, acquirerIdNum);

  return (
    <>
      <AIPageHeader
        title={intel.acquirer?.name || `Köpare #${acquirerId}`}
        subtitle={activeWorkspace?.organisation_name}
        actions={
          <div style={{ display: "flex", gap: 8 }}>
            <AIAskQadaButton
              workspaceId={activeWorkspaceId}
              prompt={buildBuyerAskPrompt({
                acquirerName: intel.acquirer?.name,
                workspaceName: activeWorkspace?.organisation_name,
              })}
              label="Fråga Qada om denna köpare"
            />
            <button
              type="button"
              className="qai-da-btn"
              onClick={() => navigate(`/deals/${activeWorkspaceId}/buyers`)}
            >
              ← Alla köpare
            </button>
          </div>
        }
      />
      <AIError message={error} />

      {/* Attention chips — deterministic observations only. */}
      {portfolioRow && (
        <div className="qai-buyer-attention-row">
          <AIBuyerAttentionChips
            row={portfolioRow}
            patternsForBuyer={acquirerPatterns}
          />
        </div>
      )}

      <div className="qai-buyer-detail-grid">
        <AIBuyerIdentityCard intel={intel} />
        <AIBuyerActivityCard intel={intel} portfolioRow={portfolioRow} />
        <AIBuyerInterestCard intel={intel} />
      </div>

      <AIBuyerFindings intel={intel} workspaceId={activeWorkspaceId} />

      <AIBuyerPatternsCard
        patterns={patterns}
        acquirerId={acquirerIdNum}
      />

      <AIBuyerActions
        acquirerId={acquirerIdNum}
        workspaceId={activeWorkspaceId}
      />

      {intel.assistive_only && (
        <div className="qai-shell-empty-hint" style={{ marginTop: 20 }}>
          {intel.assistive_only}
        </div>
      )}

      <div className="qai-shell-empty-hint" style={{ marginTop: 12 }}>
        <Link
          to={`/deals/${activeWorkspaceId}/intelligence`}
          className="qai-page-section-link"
        >
          ← Tillbaka till intelligence-vyn
        </Link>
      </div>
    </>
  );
}
